<script async setup>
import { defineProps, ref, onMounted, onBeforeUnmount } from "vue";
import KdButton from "./KdButton.vue";
import FavoritesService, { FAVORITES_EVENT } from "../services/FavoritesService.js";

const props = defineProps({
    productData: { 
        type: Object, 
        required: true,
    },
    labels: { 
        type: Array, 
        default: () => [],
    },
});

const isFavorite = ref(false);
const isHover = ref(false);

const updateFavoriteFromService = () => {
    const id = props.productData?.id;
    isFavorite.value = FavoritesService.isFavorite(id);
};

const onFavoritesChanged = () => {
    updateFavoriteFromService();
};

onMounted(() => {
    updateFavoriteFromService();

    if (typeof window !== "undefined") {
        window.addEventListener(FAVORITES_EVENT, onFavoritesChanged);
    }
});

onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
        window.removeEventListener(FAVORITES_EVENT, onFavoritesChanged);
    }
});
</script>

<template>
    <div class="bg-white shadow-md divide-y divide-black">
        <div 
            v-if="props.labels.length" 
            class="px-4 py-2 flex flex-wrap gap-2 text-sm" 
            :class="{
                'bg-black text-white': props.labels[0],
                'bg-ts-graylight text-black': props.labels[1],
                'bg-ts-red text-white': props.labels[2],
            }"
        >
            <span v-if="props.labels[0]">{{ props.labels[0] }}</span>
            <span v-else-if="props.labels[1]">{{ props.labels[1] }}</span>
            <span v-else-if="props.labels[2]">{{ props.labels[2] }}</span>
        </div>
        <div class="px-4 py-3 min-h-16 flex flex-col justify-center">
            <div class="text-sm">
                {{ props.productData.title }}
            </div>
            <div v-if="props.productData.variantLine" class="text-xs mt-1">
                {{ props.productData.variantLine }}
            </div>
        </div>
        <div class="border-y border-black">
            <div class="relative bg-white">
                <slot />
            </div>
        </div>
        <div class="px-4 py-3 border-t border-black min-h-15 flex items-center">
            <div v-if="props.productData.shortDescription" class="flex items-start gap-2 text-sm">
                <kd-icon-checkmark size="20" fill="black" class="shrink-0" />
                <span>
                    {{ props.productData.shortDescription }}
                </span>
            </div>
        </div>
        <div class="px-4 py-2 min-h-12 flex items-center gap-2">
            <span 
                class="inline-block size-3 rounded-full" 
                :class="{
                    'bg-ts-green': props.productData.stock?.status === 'instock',
                    'bg-ts-orange': props.productData.stock?.status === 'onbackorder',
                    'bg-ts-gray': props.productData.stock?.status !== 'instock' && props.productData.stock?.status !== 'onbackorder'
                }" 
            />
            <div> {{ props.productData.stock?.availability }} </div>
        </div>
        <div class="px-4 py-3 min-h-12 flex items-center">
            <div class="flex flex-row gap-2 items-baseline">
                <div v-if="props.productData.price?.regularText" class="line-through text-black/60 decoration-black decoration-2 content-center">
                    {{ props.productData.price.regularText }}
                </div>
                <div>
                    {{ props.productData.price?.saleText }}
                </div>
            </div>
        </div>
        <div class="px-4 py-3 min-h-14 flex items-center gap-3">
            <div class="flex items-center gap-3">
                <kd-button 
                    :href="props.productData.url" 
                    :icon="props.productData.icon" 
                    variant="black"
                    :title="props.productData.title" 
                    class="shrink-0"
                >
                    {{ props.productData.buttonLabel }}
                </kd-button>
                <div 
                    class="group"
                    @mouseenter="isHover = true"
                    @mouseleave="isHover = false"
                >
                    <kd-icon
                        icon="heart"
                        :filled="isFavorite || isHover"
                        size="26"
                        class="transition-transform duration-200 cursor-pointer hover:-translate-y-0.5"
                        @click="isFavorite = FavoritesService.toggle(props.productData.id)"
                    />
                </div>
                <form method="post" class="js-add-to-cart-form flex items-center" @submit.prevent>
                    <input type="hidden" name="add-to-cart" :value="props.productData.id">
                    <button 
                        type="submit" 
                        class="transition-transform duration-200 hover:-translate-y-0.5"
                        :title="`add-to-cart ${props.productData.title}`" 
                        aria-label="add-to-cart"
                    >
                        <kd-icon 
                            icon="cart-plus" 
                            fill="current" 
                            size="38"
                            class="hover:text-white hover:drop-shadow-ts-tight-black" 
                        />
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>